import defaults from "lodash/defaults";
import find from "lodash/find";
import get from "lodash/get";
import moment from "moment";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import {
  AddEventModal,
  AddPhaseTemplateModal,
  PhaseTemplatesModal,
} from "../../../parts/modals/annual-planner";
import { AddIndividualPhaseModal } from "../../../parts/modals/annual-planner/add-individial-phase-modal";
import { EditIndividualPhaseModal } from "../../../parts/modals/annual-planner/edit-individual-phase-modal";
import { EditTemplateModal } from "../../../parts/modals/annual-planner/edit-template-modal";
import { IndividualEventModal } from "../../../parts/modals/annual-planner/individual-event-modal";
import { ManageMilestoneGoalsModal } from "../../../parts/modals/annual-planner/manage-milestone-goals-modal";
import { RemoveDefaultTemplateModal } from "../../../parts/modals/annual-planner/remove-default-template-modal";
import { RemoveTemplateModal } from "../../../parts/modals/annual-planner/remove-template-modal";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import AddClientConfigurationTemplate from "../../../parts/modals/profile/AddClientConfigurationTemplate";
import AddGoal from "../../../parts/modals/profile/AddGoal";
import AddQuestionnaireTemplate from "../../../parts/modals/profile/AddQuestionnaireTemplate";
import ClientConfigurationManageTemplates from "../../../parts/modals/profile/ClientConfigurationManageTemplates";
import AccountabilityTempl from "../../../parts/modals/profile/DailyAccountability/AccountabilityTempl";
import AddAccountabilityTemplate from "../../../parts/modals/profile/DailyAccountability/AddAccountabilityTemplate";
import DailyGoalsModal from "../../../parts/modals/profile/DailyGoalsModal";
import QuestionnaireSchedule from "../../../parts/modals/profile/QuestionnaireSchedule";
import QuestionnaireTemplates from "../../../parts/modals/profile/QuestionnaireTemplates";
import SelectDailyGoalTypeModal from "../../../parts/modals/profile/SelectDailyGoalTypeModal";
import { getWeekColumnData } from "../../../utils/annual-planner-helpers";
import appendTypeAndCountry from "../../../utils/append-type-and-country";
import { isFemaleCycleSectionEnabledFromConfig } from "../../../utils/client-configuration-helpers";
import { isCoach, whoIsAthleteCoach } from "../../../utils/user-helpers";
import { resolveExerciseSetVideoModal } from "../../../utils/video-modal-url";
import TeamInviteModal from "../../coaching-portal/teams/components/TeamInviteModal";
import PreviewPhotoModal from "../calendar/progress-photos/components/PreviewPhotoModal";
import Accountability from "./components/Accountability";
import AthleteAcknowledgedModal from "./components/AthleteAcknowledgedModal";
import { ClientConfiguration } from "./components/ClientConfiguration/ClientConfiguration";
import ConfirmRemoveAthlete from "./components/ConfirmRemoveAthlete";
import ConfirmStopTraining from "./components/ConfirmStopTraining";
import CreateNoteModal from "./components/CreateNoteModal";
import { GYM_VISITS_RANGES } from "../../coaching-portal/door-access/door-visits-utils";
import tryLoad from "../../coaching-portal/door-access/try-load";
import Dashboard from "./components/Dashboard";
import Guides from "./components/Guides";
import MenstrualCycleEnabled from "./components/MenstrualCycleEnabled";
import NoteDetail from "./components/NoteDetail";
import NotesSection from "./components/NotesSection";
import Questionnaire from "./components/Questionnaire";
import VideoCenter from "./components/VideoCenter";
import VideoCenterWatch from "./components/VideoCenterWatch";
import Profile from "./Profile";
import { AnnualPlanner } from "./periodization/AnnualPlanner";

export const getRoot = async ({
  res,
  req,
  locals,
  load,
  api,
  momentTZ,
  flash,
}) => {
  const { query } = req;
  const modal = get(query, "modal", {});
  locals.athlete = appendTypeAndCountry(locals.athlete, momentTZ);

  if (query.copied) {
    flash("success", "Email address copied to clipboard");
    return res.refreshWith({ copied: false });
  }

  const { organizationOwner } = locals ?? undefined;

  const {
    section,
    templateId,
    currentConfigTemplateId,
    athlete,
    organizationCoach,
    sharedUrl,
    threadId,
    videoRecordId,
    utcDate,
    date,
    phase,
    defaultTemplateName,
    defaultTemplateId,
    templateToRemove,
    eventId,
    eventName,
    eventDescription,
    eventFromDate,
    eventToDate,
    eventColor,
    mode = "read",
    eventFrom = "",
    eventTo = "",
    track,
  } = query;

  // Check if the athlete is inactive and prohibit loading activeExercises if so
  const currentAthlete = find(
    locals.activeUser.athletes,
    (athl) => String(athl._id) === String(athlete)
  );
  const athleteInactive = currentAthlete?.status?.inactive;

  // the section is treated as absent for anyone who cannot see the tab, which keeps the
  // reports-era rule of hiding it from non coaches. This has to gate the loaders too, not
  // just the render, or they would get the periodization stats range while falling back
  // to the dashboard
  const isPeriodization =
    section === "periodization" &&
    !athleteInactive &&
    isCoach(locals.activeUser);

  if (isPeriodization) {
    defaults(query, {
      from: moment.utc().subtract(3, "months").toDate(),
      to: moment.utc().add(9, "months").toDate(),
      coach: locals.activeUser._id,
      statsFrom: getWeekColumnData(
        moment.utc().subtract(1, "years").format("YYYYMMDD"),
        locals.activeUser.startingCalendarDay,
        0
      ),
    });

    // This is made to clean the prevFrom value. Thus the data will be loaded til today's date
    // if the condition is true
    // Why? this is made because when the page is reloaded/athlete is switched, the date range
    // in the query params will remain (smthg like: 20210101 - 20220101) and we want to load the data
    // until the current date. Since prevFrom is the limitator here, we clean it
    if (
      (typeof window !== "undefined" &&
        String(window.performance.getEntriesByType("navigation")[0].type) ===
          "reload") ||
      query.athleteSwitch
    ) {
      query.prevFrom = undefined;
    }
  }

  const coachOrganizationInformation =
    locals.coachOrganizationInformation || undefined;

  // here we are getting the ID of the coach based on if the athlete is from the logged in coach or not
  const organizationCoachId = organizationCoach
    ? whoIsAthleteCoach(
        locals.activeUser.athletes,
        athlete,
        locals.activeUser._id,
        organizationCoach
      )
    : undefined;

  // if the organization coach equals the logged in coach, we are going to load the logged in coach information
  // if not, we are going to load the organization coach information
  const userToLoadInformation =
    !!organizationCoachId &&
    String(organizationCoachId) !== String(locals.activeUser._id)
      ? coachOrganizationInformation
      : locals.activeUser;

  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin" ||
      locals.organizationRoleName === "Nutritionist");

  // periodization reuses `templateId` for its own phase templates, which do not
  // live under a `${section}Templates` field on the user
  let template = null;
  if (templateId && !isPeriodization) {
    const userToUseTemplates = organizationOwner || userToLoadInformation;

    const field = `${section}Templates`;

    template = userToUseTemplates[field].templates.find(
      (item) => item._id === templateId
    );
  }

  // 30 days before the current date
  const dailyStart = moment().subtract(30, "days").format("YYYY-MM-DD");

  const loadPromises = [
    load("coachVideos", {
      athlete,
      coach: locals.activeUser._id,
    }),
    load("clientConfiguration", {
      proxyAthlete: athlete,
    }),
    load("clientGoals", {
      proxyAthlete: athlete,
    }),
    load("coachEditorNotes", {
      proxyAthlete: athlete,
    }),
    load("workflows"),
    load("onboardingQuestionnaire", {
      proxyAthlete: athlete,
    }),
    load("athleteTeam", { proxyAthlete: athlete }),
    load("athleteFlexibleProgram", { proxyAthlete: athlete }),
    load("teamPrograms"),
  ];

  if (isPeriodization) {
    const statsStart = moment(query.statsFrom, "YYYYMMDD").format("YYYYMMDD");
    const statsEnd = query.prevFrom
      ? moment(query.prevFrom, "YYYYMMDD").format("YYYYMMDD")
      : moment.utc().format("YYYYMMDD");

    loadPromises.push(
      load("phasesTemplates", {
        athleteId: athlete,
        coach: locals.activeUser._id,
      }),
      load("athleteVolumes", {
        athleteId: athlete,
        monthFrom: statsStart,
        monthTo: statsEnd,
      }),
      // `athleteStats` rather than `dailyStats`: a plain find instead of the threads
      // aggregation, and it lands on its own `locals` key so the two stat ranges no
      // longer overwrite each other
      load("athleteStats", {
        athleteId: athlete,
        monthFrom: statsStart,
        monthTo: statsEnd,
      })
    );
  } else {
    // only the dashboard and questionnaire sections read `dailyStats`, and it is the
    // expensive threads aggregation, so periodization skips it entirely
    loadPromises.push(
      load("dailyStats", {
        proxyAthlete: athlete,
        start: dailyStart,
      })
    );
  }

  if (locals.activeUser.organizationId) {
    loadPromises.push(load("usersOrganization"));
  }

  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  if (threadId) {
    await load("comments", {
      threadId,
    });
  } else {
    delete locals.comments;
  }

  if (isFemaleCycleSectionEnabledFromConfig(locals.clientConfiguration)) {
    loadPromises.push(load("femaleCycleLogs", { proxyAthlete: athlete }));
    loadPromises.push(load("femaleCycleSettings", { proxyAthlete: athlete }));
  }

  if (!athleteInactive) {
    // I swear if I hear about performance issues on the dashboard for this
    loadPromises.push(
      load("activeExercises", {
        start: moment().subtract(30, "days").startOf("week").startOf("day"),
        end: moment().endOf("day"),
        proxyAthlete: athlete,
        reqSource: "calendar",
      })
    );
  }

  if (!section || section === "dashboard") {
    loadPromises.push(
      tryLoad(load, "doorClientVisits", {
        clientId: athlete,
        range: GYM_VISITS_RANGES.includes(query.visitsRange)
          ? query.visitsRange
          : undefined,
      }).then((visits) => {
        if (visits.error) locals.doorClientVisits = visits;
      })
    );
  }

  if (query.thumbnail) {
    await load("widgetLinkThumbnail", { url: query.thumbnail });
  }

  const needsClientConfigWidgets =
    section === "client-configuration" ||
    modal.type === "add-client-configuration-template" ||
    modal.type === "client-configuration-templates";

  if (
    section === "client-configuration" ||
    (modal.type === "add-client-configuration-template" &&
      currentConfigTemplateId)
  ) {
    loadPromises.push(
      load("clientConfigurationTemplates", {
        ...(section === "client-configuration" && athlete
          ? { proxyAthlete: athlete }
          : {}),
      })
    );
  }

  if (section === "client-configuration") {
    loadPromises.push(
      load("clientConfigSectionBlocks", { proxyAthlete: athlete })
    );
    loadPromises.push(
      load("coachMonitoringSettings", { proxyAthlete: athlete })
    );
  }

  if (needsClientConfigWidgets) {
    loadPromises.push(load("clientConfigWidgetAvailability"));
  }

  await Promise.all(loadPromises);

  // Progress-photo comment thread for the "preview-photo" modal (reused
  // from Calendar's LogProgressPhotosCard) - mirrors
  // progress-photos-controller.js's own handling verbatim.
  if (modal.type === "preview-photo" && query.threadId) {
    await load("comments", { threadId: query.threadId });
  }

  // "View Video" (exercise-set-video), reused from Calendar's
  // LogTrainingCard inside Periodization's Week Plan Preview - see
  // resolveExerciseSetVideoModal's own doc comment for why this needs more
  // than a plain prop pass-through.
  const { redirectParams, resolvedThreadId, prevHref, nextHref } =
    await resolveExerciseSetVideoModal({ query, modal, locals, load, athlete });
  if (redirectParams) return res.refreshWith(redirectParams);

  const sections = {
    dashboard: <Dashboard athlete={locals.athlete} locals={locals} />,
    questionnaire: (
      <Questionnaire
        athlete={locals.athlete}
        origin={locals.origin}
        userToLoadInformation={userToLoadInformation}
        organizationCoach={organizationCoach}
        organizationOwner={organizationOwner}
      />
    ),
    accountability: (
      <Accountability
        athlete={locals.athlete}
        locals={locals}
        userToLoadInformation={userToLoadInformation}
        organizationCoach={organizationCoach}
        organizationOwner={organizationOwner}
      />
    ),
    "client-configuration": (
      <ClientConfiguration
        athlete={locals.athlete}
        startingCalendarDay={locals.activeUser.startingCalendarDay || 0}
        clientConfiguration={locals.clientConfiguration}
        activeUserToRequest={locals.activeUserToRequest}
        clientConfigurationTemplates={locals.clientConfigurationTemplates}
        clientConfigWidgetAvailability={locals.clientConfigWidgetAvailability}
        clientGoals={locals.clientGoals}
        sectionBlocks={locals.clientConfigSectionBlocks}
        coachMonitoringSettings={locals.coachMonitoringSettings}
        coachMonitoringWorkflow={locals.workflows?.find(
          (w) => w.type === "coach-daily-monitoring"
        )}
        coachPreferences={locals.activeUser}
        orgOwner={organizationOwner}
      />
    ),
    // the wrapper div is what picks up the shared section padding, which the other tabs
    // get for free by rendering a div at their root
    ...(isPeriodization
      ? {
          periodization: (
            <div className="periodization-wrapper">
              <AnnualPlanner locals={locals} />
            </div>
          ),
        }
      : {}),
    guides: (
      <Guides
        locals={locals}
        athlete={locals.athlete}
        coachOrganizationInformation={coachOrganizationInformation}
        activeUser={locals.activeUser}
      />
    ),
    coachvideos: <VideoCenter athlete={locals.athlete} locals={locals} />,
    notes: <NotesSection locals={locals} />,
  };

  // these depend on data only loaded for the periodization section, so they are
  // kept out of the map entirely on the other tabs
  const periodizationModals = isPeriodization
    ? {
        "add-event": (
          <AddEventModal
            from={eventFrom}
            to={eventTo}
            athleteId={athlete}
            date={date}
            query={query}
            locals={locals}
            organizationCoachId={organizationCoachId}
          />
        ),
        "individual-event": (
          <IndividualEventModal
            eventId={eventId}
            eventName={eventName}
            eventDescription={eventDescription}
            eventFromDate={eventFromDate}
            eventToDate={eventToDate}
            eventColor={eventColor}
            athleteId={athlete}
            mode={mode}
            organizationCoachId={organizationCoachId}
          />
        ),
        "add-phase-template": (
          <AddPhaseTemplateModal
            from={eventFrom}
            to={eventTo}
            athleteId={athlete}
            date={date}
            query={query}
            organizationCoachId={organizationCoachId}
          />
        ),
        "phase-templates": (
          <PhaseTemplatesModal
            athleteId={athlete}
            organizationCoachId={organizationCoachId}
          />
        ),
        "edit-template": (
          <EditTemplateModal
            templateId={templateId}
            athleteId={athlete}
            organizationCoachId={organizationCoachId}
          />
        ),
        "remove-template": (
          <RemoveTemplateModal
            templateId={templateId}
            athleteId={athlete}
            templateToRemove={templateToRemove}
            organizationCoachId={organizationCoachId}
          />
        ),
        "add-individual-phase": (
          <AddIndividualPhaseModal
            athleteId={athlete}
            organizationCoachId={organizationCoachId}
            track={track}
          />
        ),
        "edit-individual-phase": (
          <EditIndividualPhaseModal
            athleteId={athlete}
            phase={phase}
            organizationCoachId={organizationCoachId}
            track={track}
          />
        ),
        "remove-default-template": (
          <RemoveDefaultTemplateModal
            defaultTemplateName={defaultTemplateName}
            defaultTemplateId={defaultTemplateId}
            organizationCoachId={organizationCoachId}
          />
        ),
        "manage-milestone-goals": <ManageMilestoneGoalsModal locals={locals} />,
      }
    : {};

  locals.modal = {
    ...periodizationModals,
    "stop-training": <ConfirmStopTraining />,
    "remove-athlete": <ConfirmRemoveAthlete />,
    "accountability-templates": (
      <AccountabilityTempl
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
        startingCalendarDay={locals.activeUser.startingCalendarDay || 0}
        organizationOwner={organizationOwner}
      />
    ),
    "add-accountability-template": (
      <AddAccountabilityTemplate
        defaultTemplate={
          userToLoadInformation.accountabilityTemplates?.defaultTemplate
        }
        template={template || false}
        athleteId={locals.athlete._id}
        userToLoadInformation={userToLoadInformation}
        startingCalendarDay={locals.activeUser.startingCalendarDay || 0}
        organizationOwner={organizationOwner}
        templateId={templateId}
      />
    ),
    "questionnaire-templates": (
      <QuestionnaireTemplates
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
      />
    ),
    "questionnaire-schedule": (
      <QuestionnaireSchedule
        startingCalendarDay={locals.activeUser.startingCalendarDay || 0}
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
      />
    ),
    "add-questionnaire-template": (
      <AddQuestionnaireTemplate
        template={template || null}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
      />
    ),
    "watch-loom-video": (
      <VideoCenterWatch
        sharedUrl={sharedUrl}
        locals={locals}
        threadId={threadId}
        videoRecordId={videoRecordId}
        athleteId={locals.athlete?._id}
        utcDate={utcDate}
      />
    ),
    "activity-feed": <ActivityDrawer />,
    "add-client-goal": <AddGoal locals={locals} />,
    "daily-goal-modal": <DailyGoalsModal locals={locals} />,
    "select-daily-goal-type": <SelectDailyGoalTypeModal />,
    "female-cycle-enabled": <MenstrualCycleEnabled locals={locals} />,
    "athlete-acknowledged": <AthleteAcknowledgedModal />,
    "create-note": <CreateNoteModal locals={locals} />,
    "view-note": <NoteDetail locals={locals} />,
    "invite-to-team": <TeamInviteModal locals={locals} />,
    "client-configuration-templates": <ClientConfigurationManageTemplates />,
    "add-client-configuration-template": (
      <AddClientConfigurationTemplate templateId={currentConfigTemplateId} />
    ),
    "preview-photo": (
      <PreviewPhotoModal
        dailyStatId={query.dailyStatId}
        photo={{ type: query.photoType, pictureImg: query.pictureImg }}
        threadId={query.threadId}
        locals={locals}
        utcDate={query.utcDate}
      />
    ),
    "exercise-set-video": (
      <JWPlayerVideoModal
        athleteId={query.uploaderId || athlete}
        exerciseToEdit={query.exerciseToEdit}
        locals={locals}
        mediaId={query.mediaId}
        movement={query.movement}
        originalId={query.originalId}
        primary={query.primary}
        reviewedByTheCoach={query.reviewedByTheCoach}
        secondary={query.secondary}
        setIndex={query.setIndex}
        threadId={resolvedThreadId}
        type={query.type}
        utcDate={query.utcDate}
        videoIndex={query.videoIndex}
        prevHref={prevHref}
        nextHref={nextHref}
        setNumber={query.setNumber}
        setTarget={query.setTarget}
        setComment={query.setComment}
      />
    ),
  }[modal.type];

  res.body = (
    <Profile
      athlete={locals.athlete}
      organizationCoachId={organizationCoachId}
      api={api}
    >
      {sections[section] ? sections[section] : sections.dashboard}
    </Profile>
  );
};
